import { hasPermission } from "@mnp/shared";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, type IconName } from "../components/Icon";
import { MODULES } from "../lib/modules";
import { canSeeModule, canSeeSubscreen, useOptionalRole } from "../lib/role.tsx";
import type { Role } from "../lib/role.ts";
import { useOpsHubFeed } from "../lib/useOpsHubFeed";
import { useOpsHubStats, type OpsHubStats } from "../lib/useOpsHubStats";

type HubCardId = "reception" | "orders" | "cashbox" | "accounts" | "kb" | "warehouse";

interface HubCard {
  id: HubCardId;
  title: string;
  path: string;
  icon: IconName;
  tag: string;
  tagTone?: "soon";
  meta: string;
  canSee(role: Role): boolean;
}

const moduleById = new Map(MODULES.map((module) => [module.id, module]));
const receptionModule = moduleById.get("reception");
const cashSubscreen = receptionModule?.subscreens?.find((subscreen) => subscreen.id === "cash");

export function OpsHubScreen() {
  const role = useOptionalRole("management");
  const navigate = useNavigate();
  const statsQuery = useOpsHubStats();
  const feedQuery = useOpsHubFeed();
  const stats = statsQuery.data;
  const feed = feedQuery.data ?? [];
  const cards = useMemo(() => buildHubCards(stats).filter((card) => card.canSee(role)), [role, stats]);

  // Бейдж «Интеграции» — карточка outbox-admin-visibility (волна 4). Виден
  // только ролям с правом integrations:manage (management); право берём из
  // shared hasPermission по роли сессии — НЕ дублируем список прав на фронте.
  const canManageIntegrations = hasPermission(role, "integrations:manage");
  const integrations = stats?.integrations;
  const integrationsStuck = (integrations?.dead ?? 0) + (integrations?.stuck ?? 0);
  const showIntegrationsBadge =
    canManageIntegrations && integrations !== undefined && integrationsStuck > 0;
  const integrationsTone = (integrations?.dead ?? 0) > 0 ? "red" : "yellow";

  return (
    <main className="ops-hub">
      <section className="hub-hero" data-testid="hub-hero">
        <div>
          <p className="hub-kicker">{formatToday()}</p>
          <h1>{greeting()}</h1>
          <p className="hub-sub">
            {stats?.ordersOpen ?? "—"} заказов в работе, {stats?.ordersNeedingAction ?? "—"} требуют действий
          </p>
        </div>
        <button
          type="button"
          className="hub-settings-btn"
          aria-label="Настройки"
          data-testid="cog-button"
          onClick={() => navigate("/settings")}
        >
          <Icon name="settings" size={20} />
        </button>
      </section>

      <section className="hub-pulse" aria-label="Пульс операций">
        {pulseCards(stats).map((card) => (
          <article className="hub-pulse-card" data-testid={`pulse-card-${card.id}`} key={card.id}>
            <div className="hub-pulse-icon">
              <Icon name={card.icon} size={18} />
            </div>
            <p className="hub-pulse-label">{card.label}</p>
            <strong>{card.value}</strong>
            <span>{card.meta}</span>
          </article>
        ))}
      </section>

      <section className="hub-main-grid">
        <div>
          <div className="hub-section-head">
            <h2>Рабочие зоны</h2>
            {statsQuery.isError && <span className="hub-error">Не удалось обновить цифры</span>}
          </div>
          {showIntegrationsBadge && (
            <button
              type="button"
              className={`hub-integrations-badge tone-${integrationsTone}`}
              data-testid="integrations-badge"
              data-tone={integrationsTone}
              onClick={() => navigate("/m/integrations")}
            >
              <Icon name="alert" size={16} />
              <span>Интеграции: {integrationsStuck} застряло</span>
            </button>
          )}
          <div className="hub-workspaces">
            {cards.map((card) => {
              const isSoon = card.tagTone === "soon";
              return (
              <button
                type="button"
                className="hub-card"
                data-testid={`hub-card-${card.id}`}
                data-soon={isSoon ? "true" : undefined}
                aria-disabled={isSoon ? "true" : undefined}
                disabled={isSoon}
                title={isSoon ? "Скоро — модуль в разработке" : undefined}
                key={card.id}
                onClick={() => {
                  if (isSoon) return;
                  navigate(card.path);
                }}
              >
                <span className="hub-card-icon">
                  <Icon name={card.icon} size={20} />
                </span>
                <span className="hub-card-body">
                  <span className="hub-card-title">{card.title}</span>
                  <span className="hub-card-meta">{card.meta}</span>
                </span>
                <span className={isSoon ? "hub-card-tag tag-soon" : "hub-card-tag"}>
                  {card.tag}
                </span>
              </button>
              );
            })}
          </div>
        </div>

        <aside className="hub-feed" aria-label="Лента событий">
          <div className="hub-section-head">
            <h2>Лента событий</h2>
            {feedQuery.isLoading && <span>Обновляем</span>}
          </div>
          {feed.length === 0 ? (
            <p className="hub-feed-empty">Лента событий пока пустая</p>
          ) : (
            <ul>
              {feed.map((item) => (
                <li key={item.id} className={`hub-feed-item tone-${item.tone}`}>
                  <time>{item.time}</time>
                  <span>
                    <strong>{item.who}</strong> {item.what}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </section>
    </main>
  );
}

function buildHubCards(stats: OpsHubStats | undefined): HubCard[] {
  const activeBank = stats?.activeAccount?.bankCode ? stats.activeAccount.bankCode.toUpperCase() : "—";
  const activeHolder = stats?.activeAccount?.holderName ?? "активный счёт не выбран";
  const cashOpen = stats?.shiftOpenedAt ? "Открыта" : "Закрыта";

  return [
    {
      id: "reception",
      title: "Ресепшен",
      path: "/m/reception",
      icon: "dashboard",
      tag: "Сейчас",
      meta: `${stats?.receptionLineCount ?? "—"} человек у склада`,
      canSee: (role) => role !== "fulfillment" && Boolean(receptionModule && canSeeModule(role, receptionModule)),
    },
    {
      id: "orders",
      title: "Заказы",
      path: "/m/orders",
      icon: "orders",
      tag: `${stats?.ordersOpen ?? "—"} открыто`,
      meta: `в работе: ${stats?.ordersNeedingAction ?? "—"}`,
      canSee: (role) => Boolean(moduleById.get("orders") && canSeeModule(role, moduleById.get("orders")!)),
    },
    {
      id: "cashbox",
      title: "Касса",
      path: "/m/reception/cash",
      icon: "money",
      tag: cashOpen,
      meta: cashMeta(stats),
      canSee: (role) => Boolean(cashSubscreen && canSeeSubscreen(role, cashSubscreen)),
    },
    {
      id: "accounts",
      title: "Счета",
      path: "/m/accounts",
      icon: "chart",
      tag: "Скоро",
      tagTone: "soon",
      meta: activeHolder ?? "реквизиты счетов",
      canSee: (role) => Boolean(moduleById.get("accounts") && canSeeModule(role, moduleById.get("accounts")!)),
    },
    {
      id: "kb",
      title: "База знаний",
      path: "/m/kb",
      icon: "package",
      tag: "Скоро",
      tagTone: "soon",
      meta: "инструкции и шаблоны",
      canSee: (role) => Boolean(moduleById.get("kb") && canSeeModule(role, moduleById.get("kb")!)),
    },
    {
      id: "warehouse",
      title: "Склад",
      path: "/m/warehouse",
      icon: "truck",
      tag: "Скоро",
      tagTone: "soon",
      meta: "учёт мест на полках",
      canSee: (role) => Boolean(moduleById.get("warehouse") && canSeeModule(role, moduleById.get("warehouse")!)),
    },
  ];
}

function pulseCards(stats: OpsHubStats | undefined) {
  return [
    { id: "open", label: "Открытых заказов", value: formatMetric(stats?.ordersOpen), meta: "flex + shop", icon: "orders" as const },
    // «Ждут QR от клиента» убрана 2026-06-12: Ozon/WB-метрика (archive/ozonwb-flow);
    // серверное поле stats.awaitingQr удалено в PR5 вместе с чисткой ops-hub stats.
    { id: "pickup", label: "На выдаче · TBS", value: formatMetric(stats?.readyForPickup), meta: "склад и стойка", icon: "package" as const },
    {
      id: "turnover",
      label: "Оборот за смену",
      value: stats?.shiftTurnoverGel === null || stats?.shiftTurnoverGel === undefined ? "—" : formatNumber(stats.shiftTurnoverGel),
      meta: stats?.shiftOpenedAt ? `с ${stats.shiftOpenedAt}` : "смена закрыта",
      icon: "money" as const,
    },
  ];
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 6) return "Доброй ночи";
  if (hour < 12) return "Доброе утро";
  if (hour < 18) return "Добрый день";
  return "Добрый вечер";
}

function formatToday(): string {
  return new Intl.DateTimeFormat("ru-RU", { weekday: "long", day: "numeric", month: "long" }).format(new Date());
}

function formatMetric(value: number | undefined): string {
  return value === undefined ? "—" : String(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value);
}

function cashMeta(stats: OpsHubStats | undefined): string {
  const count = stats?.shiftOperationCount ?? null;
  const turnover = compactMoney(stats?.shiftTurnoverGel);
  const openedAt = stats?.shiftOpenedAt ?? "—";
  const countPart =
    count === null
      ? null
      : count === 0
        ? "0 сделок"
        : `${count} ${pluralDeals(count)}`;
  return [countPart, `${turnover} GEL`, `смена с ${openedAt}`].filter(Boolean).join(" · ");
}

function pluralDeals(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "сделка";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "сделки";
  return "сделок";
}

function compactMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(2).replace(".", ",")} тыс.`;
  return formatNumber(value);
}
